import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/PhoneInput';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Search, Users, UserCheck, ChevronRight, Settings2, Star } from 'lucide-react';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = ['male', 'female', 'other'];

const PatientSettingsDialog = ({ open, onOpenChange }) => {
  const [settings, setSettings] = useState({ short_code: '', city_code: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      patientsAPI.getSettings().then(r => setSettings(r.data)).catch(() => {});
    }
  }, [open]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await patientsAPI.updateSettings(settings);
      setSettings(res.data);
      toast.success('Patient ID settings updated');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="patient-settings-dialog">
        <DialogHeader>
          <DialogTitle>Patient ID Configuration</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 mb-4">
          Format: <span className="font-mono font-medium text-slate-700">[Short Code]-[City Code]-[Year]-[Number]</span>
        </p>
        <div className="space-y-4">
          <div>
            <Label>Hospital Short Code (2 letters)</Label>
            <Input
              value={settings.short_code}
              onChange={e => setSettings(s => ({ ...s, short_code: e.target.value.toUpperCase().slice(0, 2) }))}
              maxLength={2}
              placeholder="PH"
              data-testid="short-code-input"
            />
          </div>
          <div>
            <Label>City IATA Code (3 letters)</Label>
            <Input
              value={settings.city_code}
              onChange={e => setSettings(s => ({ ...s, city_code: e.target.value.toUpperCase().slice(0, 3) }))}
              maxLength={3}
              placeholder="BBI"
              data-testid="city-code-input"
            />
          </div>
          <Button onClick={handleSave} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Helpers =====
const TITLES = ['Mr', 'Mrs', 'Miss', 'Master', 'Dr', 'Other'];

const SNAP_QUESTIONS = [
  { key: 'chief_complaint', label: 'Why are you visiting today?', placeholder: 'e.g. Chest pain since 2 days' },
  { key: 'symptom_duration', label: 'How long have you had these symptoms?', placeholder: 'e.g. 3 days, 1 week' },
  { key: 'past_treatment', label: 'Any prior treatment / hospitalization for this?', placeholder: 'e.g. ER visit last month' },
  { key: 'current_medications', label: 'Any medication currently being taken?', placeholder: 'e.g. Metformin 500mg, Aspirin' },
  { key: 'referral_source', label: 'How did you hear about us / who referred you?', placeholder: 'e.g. Dr. Sharma, Google' },
];

const calcAgeFromDOB = (dobStr) => {
  if (!dobStr) return '';
  const dob = new Date(dobStr);
  if (isNaN(dob)) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 0 ? String(age) : '';
};

const dobFromAge = (ageStr) => {
  const a = parseInt(ageStr, 10);
  if (isNaN(a) || a < 0 || a > 150) return '';
  const year = new Date().getFullYear() - a;
  return `${year}-01-01`;
};

const RegisterPatientDialog = ({ open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(1); // 1 = registration form, 2 = snap card
  const [form, setForm] = useState({
    title: '', first_name: '', last_name: '', date_of_birth: '', age: '', gender: '', blood_group: '',
    phone: '', email: '', address: '', city: '', state: '', pincode: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
    allergies: '', chronic_conditions: '', marital_status: '', occupation: '',
    is_vip: false, vip_tier: '', vip_notes: '',
  });
  const [snap, setSnap] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Two-way DOB ↔ Age
  const onChangeDOB = (v) => setForm(f => ({ ...f, date_of_birth: v, age: calcAgeFromDOB(v) }));
  const onChangeAge = (v) => {
    const cleaned = v.replace(/\D/g, '');
    setForm(f => ({ ...f, age: cleaned, date_of_birth: dobFromAge(cleaned) }));
  };
  const onChangePhone = (v) => set('phone', v.replace(/\D/g, '').slice(0, 10));
  const onChangeEmergencyPhone = (v) => set('emergency_contact_phone', v.replace(/\D/g, '').slice(0, 10));

  const validateAndGotoSnap = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.date_of_birth || !form.gender || !form.phone) {
      toast.error('Please fill required fields (Title, Name, DOB, Gender, Phone)');
      return;
    }
    if (form.phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    if (form.emergency_contact_phone && form.emergency_contact_phone.length !== 10) {
      toast.error('Emergency phone must be exactly 10 digits');
      return;
    }
    setStep(2);
  };

  const submitFinal = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        allergies: form.allergies ? form.allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        chronic_conditions: form.chronic_conditions ? form.chronic_conditions.split(',').map(s => s.trim()).filter(Boolean) : [],
        snap_card: Object.values(snap).some(v => (v || '').trim()) ? { ...snap, captured_at: new Date().toISOString() } : null,
      };
      delete payload.age; // backend computes from DOB
      await patientsAPI.create(payload);
      toast.success('Patient registered successfully');
      setForm({
        title: '', first_name: '', last_name: '', date_of_birth: '', age: '', gender: '', blood_group: '',
        phone: '', email: '', address: '', city: '', state: '', pincode: '',
        emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
        allergies: '', chronic_conditions: '', marital_status: '', occupation: '',
        is_vip: false, vip_tier: '', vip_notes: '',
      });
      setSnap({});
      setStep(1);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setStep(1); setSnap({}); } onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="register-patient-dialog">
        <DialogHeader>
          <DialogTitle>{step === 1 ? 'Register New Patient' : 'Quick Snap Card'}</DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            {step === 1 ? 'Step 1 of 2 · Demographics + Contact' : 'Step 2 of 2 · 5 quick questions for the doctor'}
          </p>
        </DialogHeader>

        {step === 1 ? (
        <form onSubmit={validateAndGotoSnap} className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Select value={form.title} onValueChange={v => set('title', v)}>
                  <SelectTrigger data-testid="patient-title"><SelectValue placeholder="Mr / Mrs / Miss / Master" /></SelectTrigger>
                  <SelectContent>{TITLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div /> {/* spacer to keep grid aligned */}
              <div>
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => set('first_name', e.target.value)} required data-testid="patient-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => set('last_name', e.target.value)} required data-testid="patient-last-name" />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input type="date" value={form.date_of_birth} onChange={e => onChangeDOB(e.target.value)} required data-testid="patient-dob" />
              </div>
              <div>
                <Label>Age <span className="text-[10px] text-slate-400">(auto-calculated, editable)</span></Label>
                <Input value={form.age} onChange={e => onChangeAge(e.target.value)} placeholder="auto from DOB" inputMode="numeric" maxLength={3} data-testid="patient-age" />
              </div>
              <div>
                <Label>Gender *</Label>
                <Select value={form.gender} onValueChange={v => set('gender', v)}>
                  <SelectTrigger data-testid="patient-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => <SelectItem key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Blood Group</Label>
                <Select value={form.blood_group} onValueChange={v => set('blood_group', v)}>
                  <SelectTrigger data-testid="patient-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {BLOOD_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone * <span className="text-[10px] text-slate-400">(10 digits)</span></Label>
                <PhoneInput value={form.phone} onChange={v => setForm({ ...form, phone: v })} required testid="patient-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="patient-email" />
              </div>
              <div>
                <Label>Marital Status</Label>
                <Select value={form.marital_status} onValueChange={v => set('marital_status', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['single', 'married', 'divorced', 'widowed'].map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Occupation</Label>
                <Input value={form.occupation} onChange={e => set('occupation', e.target.value)} />
              </div>
            </div>
          </div>

          {/* VIP block */}
          <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="patient-vip-toggle">
              <input type="checkbox" checked={form.is_vip} onChange={e => set('is_vip', e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
              <div className="flex-1">
                <span className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                  <Star size={14} className="fill-amber-500 text-amber-500" /> VIP Patient
                </span>
                <p className="text-[11px] text-amber-700/80 mt-0.5">VIP status flags this patient across queues, appointments, billing and pharmacy so the entire team can offer priority handling.</p>
              </div>
            </label>
            {form.is_vip && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs">VIP Tier</Label>
                  <Select value={form.vip_tier} onValueChange={v => set('vip_tier', v)}>
                    <SelectTrigger data-testid="patient-vip-tier"><SelectValue placeholder="Select tier (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="platinum">Platinum</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="corporate">Corporate Account</SelectItem>
                      <SelectItem value="celebrity">Celebrity / Public Figure</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">VIP Notes</Label>
                  <Input value={form.vip_notes} onChange={e => set('vip_notes', e.target.value)} placeholder="Brief handling note" data-testid="patient-vip-notes" />
                </div>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Street Address</Label>
                <Input value={form.address} onChange={e => set('address', e.target.value)} />
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={e => set('state', e.target.value)} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => set('pincode', e.target.value)} /></div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} /></div>
              <div><Label>Phone <span className="text-[10px] text-slate-400">(10 digits)</span></Label><PhoneInput value={form.emergency_contact_phone} onChange={v => setForm({ ...form, emergency_contact_phone: v })} testid="patient-emergency-phone" /></div>
              <div><Label>Relation</Label><Input value={form.emergency_contact_relation} onChange={e => set('emergency_contact_relation', e.target.value)} /></div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Medical Info</h4>
            <div className="space-y-3">
              <div><Label>Allergies (comma-separated)</Label><Textarea value={form.allergies} onChange={e => set('allergies', e.target.value)} placeholder="e.g. Penicillin, Peanuts" rows={2} /></div>
              <div><Label>Chronic Conditions (comma-separated)</Label><Textarea value={form.chronic_conditions} onChange={e => set('chronic_conditions', e.target.value)} placeholder="e.g. Diabetes, Hypertension" rows={2} /></div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="register-patient-next-btn">
            Next: Snap Card →
          </Button>
        </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              Ask the patient these 5 quick questions. They give the doctor full context before the consultation — and you can skip any field that isn't applicable.
            </div>
            {SNAP_QUESTIONS.map(q => (
              <div key={q.key}>
                <Label className="text-sm">{q.label}</Label>
                <Textarea value={snap[q.key] || ''} onChange={e => setSnap(s => ({ ...s, [q.key]: e.target.value }))} placeholder={q.placeholder} rows={2} data-testid={`snap-${q.key}`} />
              </div>
            ))}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} disabled={loading}>← Back</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setSnap({}); submitFinal(); }} disabled={loading} data-testid="skip-snap-btn">Skip & Register</Button>
                <Button onClick={submitFinal} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="register-patient-btn">
                  {loading ? 'Registering...' : 'Save & Register'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PatientsPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const isSuperAdmin = JSON.parse(localStorage.getItem('user') || '{}')?.is_super_admin;
  const hasHospitalContext = isSuperAdmin ? !!localStorage.getItem('selectedHospitalId') : true;

  const fetchPatients = useCallback(async () => {
    if (!hasHospitalContext) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const [pRes, cRes] = await Promise.all([
        patientsAPI.list(params),
        patientsAPI.count(),
      ]);
      setPatients(pRes.data);
      setCounts(cRes.data);
    } catch (e) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [search, hasHospitalContext]);

  useEffect(() => {
    const t = setTimeout(fetchPatients, 300);
    return () => clearTimeout(t);
  }, [fetchPatients]);

  return (
    <div className="space-y-6" data-testid="patients-page">
      {isSuperAdmin && !hasHospitalContext && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
              <Settings2 size={22} className="text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-800">No Hospital Selected</p>
              <p className="text-sm text-amber-600">Use the hospital selector in the sidebar to choose which hospital to manage.</p>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Patient Management</h1>
          <p className="text-sm text-slate-500 mt-1">Register and manage hospital patients</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="patient-settings-btn">
            <Settings2 size={16} className="mr-2" /> ID Config
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowRegister(true)} data-testid="register-new-patient-btn">
            <UserPlus size={16} className="mr-2" /> Register Patient
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="h-12 w-12 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Users size={22} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="total-patients-count">{counts.total}</p>
              <p className="text-sm text-slate-500">Total Patients</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="h-12 w-12 rounded-lg bg-emerald-50 flex items-center justify-center">
              <UserCheck size={22} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="active-patients-count">{counts.active}</p>
              <p className="text-sm text-slate-500">Active Patients</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Patient Registry</CardTitle>
            <div className="relative w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, ID, phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="patient-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">Loading...</div>
          ) : patients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Users size={40} className="mb-3 opacity-40" />
              <p>No patients found</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Patient ID</TableHead>
                    <TableHead className="font-semibold text-slate-700">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Phone</TableHead>
                    <TableHead className="font-semibold text-slate-700">Gender</TableHead>
                    <TableHead className="font-semibold text-slate-700">Blood Group</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map(p => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(`/patients/${p.id}`)}
                      data-testid={`patient-row-${p.patient_id}`}
                    >
                      <TableCell className="font-mono text-sm font-medium text-emerald-700">{p.patient_id}</TableCell>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          {p.is_vip && (
                            <span title={`VIP${p.vip_tier ? ` · ${p.vip_tier}` : ''}`} data-testid={`vip-${p.patient_id}`}>
                              <Star size={14} className="fill-amber-400 text-amber-500 flex-shrink-0" />
                            </span>
                          )}
                          <span>{p.title ? `${p.title} ` : ''}{p.first_name} {p.last_name}</span>
                        </span>
                      </TableCell>
                      <TableCell>{p.phone}</TableCell>
                      <TableCell className="capitalize">{p.gender}</TableCell>
                      <TableCell>{p.blood_group || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={p.is_active ? 'default' : 'secondary'} className={p.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell><ChevronRight size={16} className="text-slate-400" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <RegisterPatientDialog open={showRegister} onOpenChange={setShowRegister} onCreated={fetchPatients} />
      <PatientSettingsDialog open={showSettings} onOpenChange={setShowSettings} />
    </div>
  );
};

export default PatientsPage;
